import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiAward } from 'react-icons/fi';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import LiveChallengeAuthor from './LiveChallengeAuthor';

const MEDAL_BG = ['bg-[#FFD700]', 'bg-neutral-300', 'bg-[#CD7F32]', 'bg-neutral-lightest'];

const FinalResultPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { session, leaderboard: contextLeaderboard, unsubscribe, resetState } = useLiveChallenge();

  const challengeId = id || session?.live_challenge_id;
  const [myScore, setMyScore] = useState(null);
  const [fullLeaderboard, setFullLeaderboard] = useState([]);
  const [challengeUser, setChallengeUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [scoreRes, lbRes, showRes] = await Promise.all([liveChallengesApi.myScore(challengeId).catch(() => null), liveChallengesApi.leaderboard(challengeId, 100).catch(() => null), liveChallengesApi.show(challengeId).catch(() => null)]);

        if (scoreRes?.data) {
          const raw = scoreRes.data;
          const attrs = raw.attributes || {};
          setMyScore({
            rank: attrs.rank,
            total_score: attrs['total-score'] ?? attrs.total_score ?? 0,
            user_name: attrs.user?.name
          });
        }

        if (lbRes?.data) {
          const items = Array.isArray(lbRes.data) ? lbRes.data : [];
          setFullLeaderboard(
            items.map(it => {
              const a = it.attributes || {};
              return {
                id: it.id,
                user_id: a.user?.id,
                name: a.user?.name,
                total_score: a['total-score'] ?? a.total_score ?? 0,
                rank: a.rank
              };
            })
          );
        }

        if (showRes?.data?.attributes?.user) {
          setChallengeUser(showRes.data.attributes.user);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [challengeId]);

  useEffect(() => {
    return () => {
      unsubscribe();
      resetState();
    };
  }, [unsubscribe, resetState]);

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const leaderboard = fullLeaderboard.length > 0 ? fullLeaderboard : contextLeaderboard || [];
  const finalRank = myScore?.rank ?? '—';
  const finalPoints = myScore?.total_score ?? 0;
  const isTopThree = typeof finalRank === 'number' && finalRank <= 3;

  return (
    <div className="flex min-h-screen flex-col items-center bg-bright px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <div className="flex flex-col items-center gap-4 rounded-2xl bg-white px-8 py-10 text-center shadow-dropshadow-lg">
          <div className={`flex h-20 w-20 items-center justify-center rounded-full text-white shadow-dropshadow-md ${isTopThree ? MEDAL_BG[finalRank - 1] : 'bg-primary'}`}>
            <FiAward className="h-10 w-10" />
          </div>

          <h1 className="m-0 text-2xl font-extrabold text-foreground">Desafio Finalizado!</h1>

          <div className="grid w-full grid-cols-2 gap-3 pt-2">
            <div className="flex flex-col items-center gap-1 rounded-xl bg-primary-lightest px-4 py-4">
              <span className="text-3xl font-extrabold text-primary-dark">{typeof finalRank === 'number' ? `${finalRank}º` : finalRank}</span>
              <span className="text-xs font-semibold text-neutral">lugar</span>
            </div>
            <div className="flex flex-col items-center gap-1 rounded-xl bg-success-lightest px-4 py-4">
              <span className="text-3xl font-extrabold text-success">{finalPoints}</span>
              <span className="text-xs font-semibold text-neutral">pontos</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="m-0 text-lg font-bold text-foreground">Classificação geral</h2>
          {loading ? (
            <p className="m-0 text-center text-sm text-neutral-light">Carregando...</p>
          ) : leaderboard.length > 0 ? (
            <Card
              border
              orientation="vertical"
              className="px-0 py-0"
            >
              <ul className="m-0 list-none pl-0">
                {leaderboard.map((entry, idx) => {
                  const rank = entry.rank ?? idx + 1;
                  const isMe = rank === finalRank;
                  const medalBg = idx < 3 ? MEDAL_BG[idx] : MEDAL_BG[3];
                  const medalText = idx < 3 ? 'text-white' : 'text-neutral-dark';
                  return (
                    <li
                      key={entry.id ?? entry.user_id ?? idx}
                      className={`flex items-center justify-between border-b border-solid border-neutral-lightest px-4 py-3 last:border-0 ${isMe ? 'bg-primary-lightest/40' : ''}`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${medalBg} ${medalText}`}>{rank}</span>
                        <span className={`truncate text-sm ${isMe ? 'font-bold text-primary-dark' : 'font-medium text-foreground'}`}>
                          {entry.name || entry.user_name || `Participante ${rank}`}
                          {isMe && <span className="ml-2 text-xs text-primary">(você)</span>}
                        </span>
                      </div>
                      <span className="ml-2 text-sm font-bold tabular-nums text-primary">{entry.total_score ?? 0}</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : (
            <p className="m-0 text-center text-sm text-neutral-light">Nenhum resultado disponível.</p>
          )}
        </div>

        <Button
          type="button"
          variant="default"
          size="lg"
          fullWidth
          onClick={handleGoHome}
        >
          Voltar para o início
        </Button>

        {challengeUser && (
          <LiveChallengeAuthor
            user={challengeUser}
            size="sm"
            variant="light"
            className="mt-2 justify-center"
          />
        )}
      </div>
    </div>
  );
};

export default FinalResultPage;
